import React, { useState } from 'react';
import { Trash2, X, AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../lib/utils';
import { primaryText, semiBoldText, baseTextSize, smallTextSize } from '../../../../styling/stylingHelper';
import { useRecipeStore } from '../../../../stores/appRecipeStore';

interface RecipeDeleteAllModalProps {
  restaurantId: string;
  recipeCount: number;
  onClose: () => void;
  onSuccess: () => void;
}

const RecipeDeleteAllModal: React.FC<RecipeDeleteAllModalProps> = ({
  restaurantId,
  recipeCount,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const deleteAllRecipesData = useRecipeStore((s) => s.deleteAllRecipesData);

  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAllRecipesData(restaurantId);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('recipes.deleteAllModal.errorGeneric'));
      setIsDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black dark:bg-black bg-opacity-50 dark:bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-background dark:bg-background-dark rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
            </div>
            <h2 className={cn(primaryText, semiBoldText, baseTextSize)}>
              {t('recipes.deleteAllModal.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className={cn(primaryText, smallTextSize, 'text-gray-600 dark:text-gray-400')}>
            {t('recipes.deleteAllModal.summary', { count: recipeCount })}
          </p>

          {/* Warning */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
              {t('recipes.deleteAllModal.warning')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            >
              {t('recipes.deleteAllModal.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isDeleting}
              className="flex-1 px-6 py-3 bg-red-600 dark:bg-red-700 text-white dark:text-white rounded-lg font-medium hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('recipes.deleteAllModal.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  {t('recipes.deleteAllModal.confirmDelete')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeDeleteAllModal;
